import React, { useState } from 'react';
import { Alert, TextField, Typography } from '@mui/material';
import ConfirmActionDialog from './ConfirmActionDialog';
import { useHasChanged } from '@hooks/useHasChanged';

const CONFIRM_WORD = 'archive';

interface ArchiveDeviceDialogProps {
  open: boolean;
  deviceName?: string;
  isArchiving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Confirmation dialog requiring the user to type "archive" before archiving. */
const ArchiveDeviceDialog: React.FC<ArchiveDeviceDialogProps> = ({
  open,
  deviceName,
  isArchiving,
  onClose,
  onConfirm,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText.toLowerCase() === CONFIRM_WORD;
  const hasMismatch = confirmText !== '' && !isConfirmed;

  // Reset the field only once the dialog is fully dismissed. Keeping it while
  // open means a failed archive (dialog stays open) leaves the typed word in
  // place so the user can retry without re-typing.
  if (useHasChanged(open) && !open) setConfirmText('');

  return (
    <ConfirmActionDialog
      open={open}
      title="Archive Device"
      confirmLabel="Archive Device"
      loadingLabel="Archiving..."
      isLoading={isArchiving}
      confirmDisabled={!isConfirmed}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <Alert severity="warning" sx={{ mb: 2 }}>
        The device "{deviceName}" will be archived and hidden from the device list. This action can be reversed by contacting an administrator.
      </Alert>
      <Typography variant="body2" sx={{ mb: 2 }}>
        To confirm archiving, please type <strong>archive</strong> below:
      </Typography>
      <TextField
        autoFocus
        fullWidth
        label="Type 'archive' to confirm"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        error={hasMismatch}
        helperText={hasMismatch ? 'Please type "archive" to confirm' : ''}
      />
    </ConfirmActionDialog>
  );
};

export default ArchiveDeviceDialog;
